import { useIntl } from 'react-intl';
import * as z from 'zod';

import { MappedForm } from '@/components/elements/form-map/form-map.component';
import { Product, TemplatePublicationStatus } from '@/gql/graphql';
import { useProducts } from '@/hooks/use-products/use-products.hook';
import { useToast } from '@/hooks/use-toast.hook';
import { allProducts } from '@/pages/products/products.requests';
import { useDialogStore } from '@/store/dialog.store';
import { useMutation, useQuery } from '@apollo/client';
import { allTemplates, createTemplate, updateTemplate } from '../../../templates.requests';
import { templateMap } from './template-form.config';
import { templateFormMessages } from './template-form.messages';
import { templateFormSchema } from './template-form.validation';

export type TemplateFormInitialValues = {
  id: string;
  name: string;
  description: string;
  copyAnswersFrom: string;
};

type TemplateFormProps = {
  context?: string;
  edit?: boolean;
  initialValues?: Partial<TemplateFormInitialValues>;
  afterAdd?: () => void;
};

export const TemplateForm = ({ initialValues, edit, afterAdd, context }: TemplateFormProps) => {
  const intl = useIntl();
  const { toast } = useToast();

  const { data: productResponse } = useQuery<{ products: Product[] }>(allProducts);

  const { products } = useProducts({
    initialProducts: productResponse?.products,
  });

  const [createTemplateMutation, { loading: createTemplateLoading }] = useMutation(createTemplate);
  const [editTemplateMutation, { loading: editTemplateLoading }] = useMutation(updateTemplate);

  const { handleDialogClose } = useDialogStore((state) => ({
    handleDialogClose: state.closeDialog,
  }));

  const handleSubmit = async (values: z.infer<ReturnType<typeof templateFormSchema>>) => {
    if (edit) {
      if (!initialValues) return;
      await editTemplateMutation({
        variables: {
          updateTemplateInput: {
            id: initialValues.id || '',
            name: values.name,
            description: values.description,
            publicationStatus: TemplatePublicationStatus.Private,
          },
        },
        refetchQueries: [{ query: allTemplates }],
      });
      handleDialogClose();
      toast({
        description: intl.formatMessage(templateFormMessages.editTemplateToast),
        variant: 'default',
      });
    } else {
      await createTemplateMutation({
        variables: {
          createTemplateInput: {
            name: values.name,
            description: values.description,
            copyAnswersFrom: values.copyAnswersFrom,
          },
        },
        refetchQueries: [{ query: allTemplates }],
      });
      handleDialogClose();
      toast({
        description: intl.formatMessage(templateFormMessages.addTemplateToast),
        variant: 'default',
      });
    }
    if (afterAdd) afterAdd();
  };

  return (
    <MappedForm
      context="template"
      loading={createTemplateLoading || editTemplateLoading}
      formMap={templateMap(intl, products, edit, context)}
      defaultValues={{
        name: '',
        description: '',
      }}
      initialValues={initialValues}
      onClose={handleDialogClose}
      onSubmit={handleSubmit}
      validationSchema={templateFormSchema}
    />
  );
};
